import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import OCRApiService from '../services/api';
import toast from 'react-hot-toast';
import { validateSearchTerm } from '../utils/searchValidation';

interface POLookupProps {
  poNumber: string;
  vendorNumber?: string;
  onPOSelect: (poNumber: string, poDetails?: any) => void;
  onPOChange?: (poNumber: string) => void;
  disabled?: boolean;
  className?: string;
}

const POLookup: React.FC<POLookupProps> = ({
  poNumber,
  vendorNumber,
  onPOSelect,
  onPOChange,
  disabled = false,
  className = ''
}) => {
  const [searchTerm, setSearchTerm] = useState(poNumber || '');
  const [isSearching, setIsSearching] = useState(false);
  const [isLookingUp, setIsLookingUp] = useState(false);
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [showResults, setShowResults] = useState(false);
  const [selectedPO, setSelectedPO] = useState<any>(null);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
  
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const lookupInFlightRef = useRef<boolean>(false);

  // Update dropdown position
  const updateDropdownPosition = useCallback(() => {
    if (inputRef.current) {
      const rect = inputRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + window.scrollY,
        left: rect.left + window.scrollX,
        width: rect.width
      });
    }
  }, []);

  // Update position when showing results
  useEffect(() => {
    if (showResults) {
      updateDropdownPosition();
    }
  }, [showResults, updateDropdownPosition]);

  // Reposition dropdown on window resize + any scroll (capture phase catches inner scroll containers)
  useEffect(() => {
    if (!showResults) return;
    const reposition = () => updateDropdownPosition();
    window.addEventListener('resize', reposition);
    window.addEventListener('scroll', reposition, true);
    return () => {
      window.removeEventListener('resize', reposition);
      window.removeEventListener('scroll', reposition, true);
    };
  }, [showResults, updateDropdownPosition]);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        inputRef.current && 
        !inputRef.current.contains(event.target as Node) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setShowResults(false);
      }
    };

    if (showResults) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showResults]);

  // Sync searchTerm with poNumber prop
  useEffect(() => {
    setSearchTerm(poNumber || '');
  }, [poNumber]);

  // Debounced search for autocomplete
  const debouncedSearch = useCallback(
    debounce(async (query: string, vendorFilter?: string) => {
      const hasVendor = !!(vendorFilter && vendorFilter.trim().length > 0);
      // Allow empty query only when a vendor is selected (browse-all-for-vendor mode)
      if (query.length >= 2 || hasVendor) {
        setIsSearching(true);
        try {
          const response = await OCRApiService.searchPOs(query, 10, vendorFilter);
          if (response.success && response.results) {
            setSearchResults(response.results);
            // Only pop the dropdown open if the user is actually on this field.
            // Background prefetches (triggered by vendor change while user is elsewhere)
            // just cache results silently.
            const isFocused = document.activeElement === inputRef.current;
            if (response.results.length > 0 && isFocused) {
              updateDropdownPosition();
              setShowResults(true);
            } else if (!isFocused) {
              setShowResults(false);
              // Fuzzy-suffix auto-correct: when OCR extracts a bare PO like
              // "510910" and the backend fuzzy-resolved it to "510910-27",
              // silently correct the field to the matched value so the clerk
              // sees the actual PO (and downstream lookups use it). Only
              // fires when the match differs from input by exactly the
              // FY suffix "-\d{2}" and no PO is currently selected.
              const q = (query || '').trim();
              if (
                !selectedPO &&
                response.results.length === 1 &&
                q.length > 0 &&
                response.results[0]?.po_number &&
                response.results[0].po_number !== q
              ) {
                const matched = String(response.results[0].po_number);
                const fySuffix = /^(.*)-\d{2}$/.exec(matched);
                if (fySuffix && fySuffix[1] === q) {
                  setSearchTerm(matched);
                  if (onPOChange) onPOChange(matched);
                }
              }
            }
          } else {
            setSearchResults([]);
            setShowResults(false);
          }
        } catch (error) {
          console.error('PO search failed:', error);
          setSearchResults([]);
          setShowResults(false);
        } finally {
          setIsSearching(false);
        }
      } else {
        setSearchResults([]);
        setShowResults(false);
      }
    }, 300),
    []
  );

  // Effect to trigger search when search term or vendor filter changes
  useEffect(() => {
    debouncedSearch(searchTerm, vendorNumber);
  }, [searchTerm, vendorNumber, debouncedSearch]);

  const handleSearchTermChange = (value: string) => {
    setSearchTerm(value);
    setValidationError(null);
    if (onPOChange) {
      onPOChange(value);
    }
    setSelectedPO(null);
    setShowResults(false);
  };

  // Lookup PO (exact match) - similar to vendor lookup
  const handlePOLookup = async () => {
    const validation = validateSearchTerm(searchTerm);
    setValidationError(validation.error);
    if (!validation.isValid) return;
    const sanitizedTerm = validation.sanitized;
    setValidationError(null);

    if (lookupInFlightRef.current) return;
    lookupInFlightRef.current = true;
    setIsLookingUp(true);
    setSelectedPO(null);

    try {
      const response = await OCRApiService.lookupPO(sanitizedTerm, vendorNumber);

      if (response.success && response.match_found && response.po_details) {
        const poDetails = response.po_details;
        setSelectedPO(poDetails);
        setSearchTerm(poDetails.po_number || searchTerm);
        setShowResults(false);
       // onPOSelect(poDetails.po_number || searchTerm, poDetails);
        toast.success('PO found and loaded');
      } else {
        toast.error(response.message || 'PO not found. You can still enter the PO number manually.');
      }
    } catch (error) {
      console.error('PO lookup failed:', error);
      toast.error('Failed to lookup PO');
    } finally {
      setIsLookingUp(false);
      lookupInFlightRef.current = false;
    }
  };

  const handlePOSelection = (po: any) => {
    if (!po || !po.po_number) {
      console.error('Invalid PO data:', po);
      return;
    }
    
    setSelectedPO(po);
    setSearchTerm(po.po_number);
    setShowResults(false);
    setSearchResults([]);
    onPOSelect(po.po_number, po);
    if (onPOChange) {
      onPOChange(po.po_number);
    }
  };

  return (
    <div className={`po-lookup ${className}`}>
      <div className="flex gap-2">
        <div className="flex-1 relative z-30">
          <input
            ref={inputRef}
            type="text"
            value={searchTerm}
            onChange={(e) => handleSearchTermChange(e.target.value)}
            onFocus={() => {
              setShowResults(searchResults.length > 0);
              updateDropdownPosition();
            }}
            onBlur={() => {
              setTimeout(() => setShowResults(false), 200);
              const trimmed = searchTerm.trim();
              if (
                trimmed.length >= 2 &&
                !lookupInFlightRef.current &&
                !(selectedPO && selectedPO.po_number === searchTerm)
              ) {
                handlePOLookup();
              }
            }}
            placeholder="Enter PO number..."
            disabled={disabled}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent bg-white"
            autoComplete="off"
          />
          
          {/* Portal-based Search Results Dropdown */}
          {showResults && searchResults.length > 0 && createPortal(
            <div 
              ref={dropdownRef}
              className="fixed bg-white border-2 border-gray-300 rounded-md shadow-2xl max-h-60 overflow-y-auto"
              style={{ 
                top: dropdownPosition.top,
                left: dropdownPosition.left,
                width: dropdownPosition.width,
                zIndex: 99999
              }}
            >
              {searchResults.filter(po => po && po.po_number).map((po, index) => (
                <div
                  key={po.po_number || index}
                  onClick={() => handlePOSelection(po)}
                  className="px-3 py-2 bg-white hover:bg-gray-100 cursor-pointer border-b border-gray-100 last:border-b-0"
                >
                  <div className="font-medium text-gray-900 break-all">PO: <span className="font-mono">{po.po_number}</span></div>
                  <div className="text-sm text-gray-600">
                    {po.vendor?.name && (
                      <div className="flex items-center justify-between">
                        <span>Vendor: {po.vendor.name}</span>
                        {po.vendor.vendor_number && (
                          <span className="text-xs text-gray-500">#{po.vendor.vendor_number}</span>
                        )}
                      </div>
                    )}
                    {po.total_amount && (
                      <div className="text-xs text-gray-500 mt-1">
                        Total: ${po.total_amount.toFixed(2)}
                      </div>
                    )}
                    {po.purchase_order_date && (
                      <div className="text-xs text-gray-500">
                        Date: {po.purchase_order_date}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>,
            document.body
          )}
          
          {isSearching && (
            <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-brand-500"></div>
            </div>
          )}
        </div>
        
        <button
          onClick={handlePOLookup}
          disabled={disabled || isLookingUp || !searchTerm.trim()}
          className="px-4 py-2 bg-brand-600 text-white rounded-md hover:bg-brand-700 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center gap-2"
        >
          {isLookingUp ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              Looking up...
            </>
          ) : (
            'Lookup'
          )}
        </button>
      </div>

      {/* Validation Error */}
      {validationError && (
        <p className="mt-1 text-sm text-red-600">{validationError}</p>
      )}

      {/* Selected PO Info */}
      {selectedPO && (
        <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-md">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-2 h-2 bg-green-500 rounded-full"></div>
            <span className="text-sm font-medium text-green-800">
              PO Selected
            </span>
          </div>
          <div className="text-sm text-green-700">
            <div><strong>PO Number:</strong> {selectedPO.po_number || searchTerm}</div>
            {selectedPO.vendor?.name && (
              <div><strong>Vendor:</strong> {selectedPO.vendor.name}</div>
            )}
            {selectedPO.vendor?.vendor_number && (
              <div><strong>Vendor Number:</strong> {selectedPO.vendor.vendor_number}</div>
            )}
            {selectedPO.purchase_order_date && (
              <div><strong>PO Date:</strong> {selectedPO.purchase_order_date}</div>
            )}
            {selectedPO.total_amount && (
              <div><strong>Total Amount:</strong> ${selectedPO.total_amount.toFixed(2)}</div>
            )}
            {selectedPO.line_items && selectedPO.line_items.length > 0 && (
              <div><strong>Line Items:</strong> {selectedPO.line_items.length} item(s)</div>
            )}
          </div>
        </div>
      )}

      {/* No matches message */}
      {searchResults.length === 0 && searchTerm && !isSearching && !selectedPO && searchTerm.length >= 2 && (
        <div className="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
          <div className="text-sm text-yellow-800">
            No PO matches found for "{searchTerm}". Click "Lookup" to search for exact match or enter manually.
          </div>
        </div>
      )}
    </div>
  );
};

// Debounce utility function
function debounce<T extends (...args: any[]) => any>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeout: NodeJS.Timeout | null = null;
  
  return function executedFunction(...args: Parameters<T>) {
    const later = () => {
      timeout = null;
      func(...args);
    };
    
    if (timeout) {
      clearTimeout(timeout);
    }
    timeout = setTimeout(later, wait);
  };
}

export default POLookup;

